// src/pages/ManageStudents.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import "../App.css";
import "aos/dist/aos.css";
import AOS from "aos";
import toast, { Toaster } from "react-hot-toast";
import { useMediaQuery } from "@mui/material";

import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

/* ---------- Seat-cap helpers (schools/*) ---------- */
function seatLimitFromSchoolDoc(schoolDoc) {
  const students = Number(schoolDoc?.package?.students || 0);
  return students > 0 ? students : 50;
}

/* ---------- Email helpers ---------- */
const DOMAIN = "yourstoryworld.com";

// keep only [a-z0-9], collapse, ensure length >= 3, clip to 40
function sanitizeLocalPart(s) {
  const cleaned = String(s || "")
    .toLowerCase()
    .normalize("NFKD")               // drop accents
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^a-z0-9]/g, "");      // keep only a-z0-9
  let local = cleaned.slice(0, 40);
  if (local.length < 3) local = `student${Math.floor(Math.random() * 10000)}`;
  return local;
}

function toEmailFromNames(firstName, lastName) {
  const a = sanitizeLocalPart(firstName);
  const b = sanitizeLocalPart(String(lastName).charAt(0));
  // add small random suffix to avoid collisions on rapid adds
  const suffix = Math.floor(Math.random() * 1000);
  const local = `${(a + b).slice(0, 32)}${suffix}`; // ≤ 35-ish total
  return `${local}@${DOMAIN}`;
}

function uiUsernameFromEmail(email) {
  const at = String(email).indexOf("@");
  return at > 0 ? email.slice(0, at) : String(email);
}

const ManageStudents = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const schoolId = localStorage.getItem("schoolId") || "";

  const [students, setStudents] = useState([]);
  const [newStudent, setNewStudent] = useState({ firstName: "", lastName: "", className: "" });
  const [bulkText, setBulkText] = useState("");
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedClass, setSelectedClass] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [includeStats, setIncludeStats] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [seatLimit, setSeatLimit] = useState(50);

  // submit guards
  const [adding, setAdding] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [csvAdding, setCsvAdding] = useState(false);
  const [resettingId, setResettingId] = useState(null);


  const functions = getFunctions(undefined, "us-central1");
  const createStudentCallable = httpsCallable(functions, "createStudentSeatChecked");
  const deleteStudentCallable = httpsCallable(functions, "deleteStudentSeatCounted");
   const resetPasswordCallable = httpsCallable(functions, "adminResetStudentPassword");


  // one-time warn guard so we don't spam toasts for non-critical listeners
  const warnedStudentsListener = useRef(false);
  const warnedSchoolListener = useRef(false);

  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  useEffect(() => {
  // jump to top when arriving on this page
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });

  // if your scroll is on a container instead of window, also reset it:
  const el = document.querySelector(".page-container");
  if (el) el.scrollTop = 0;

  const main = document.querySelector(".page-content");
  if (main) main.scrollTop = 0;
}, []);


  // School doc (for seat cap)
  useEffect(() => {
    if (!schoolId) return;
    const ref = doc(db, "schools", schoolId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const sdoc = snap.exists() ? snap.data() : null;
        setSeatLimit(seatLimitFromSchoolDoc(sdoc));
      },
      (err) => {
        if (!warnedSchoolListener.current) {
          console.warn("schools seat listener error:", err);
          warnedSchoolListener.current = true;
        }
      }
    );
    return () => unsub();
  }, [schoolId]);

  // Live student list — ONLY schoolId
  useEffect(() => {
    if (!schoolId) return;
    const qSch = query(collection(db, "students"), where("schoolId", "==", schoolId));
    const unsub = onSnapshot(
      qSch,
      (snap) => {
        const incoming = snap.docs.map((d) => {
          const data = d.data() || {};
          return { id: d.id, uid: data.uid || d.id, ...data };
        });
        // de-dupe by uid (preserve in-memory password if any)
        setStudents((prev) => {
          const prevPwd = new Map(prev.map((s) => [s.uid || s.id, s.password]));
          const byUid = new Map();
          incoming.forEach((s) => {
            const uid = s.uid || s.id;
            const withPwd = prevPwd.get(uid) ? { ...s, password: prevPwd.get(uid) } : s;
            byUid.set(uid, withPwd);
          });
          return Array.from(byUid.values());
        });
      },
      (e) => {
        if (!warnedStudentsListener.current) {
          console.warn("students onSnapshot error:", e?.code || e?.message || e);
          warnedStudentsListener.current = true;
        }
      }
    );
    return () => unsub();
  }, [schoolId]);

  const seatsUsed = students.length;
  const seatsRemaining = Math.max(seatLimit - seatsUsed, 0);
  const isFull = seatsRemaining <= 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStudent((s) => ({ ...s, [name]: value }));
  };

  const msgFromError = (err) => {
    if (!err) return "Unknown error";
    if (typeof err?.message === "string") return err.message;
    return String(err?.code || "Unknown error");
  };

  /* ---- Users mirror for rules/role checks ---- */
  async function stampStudentUserDoc(uid, { firstName, lastName, email }) {
    await setDoc(
      doc(db, "users", uid),
      {
        uid,
        email: email || "",
        emailLower: (email || "").toLowerCase(),
        role: "student",
        orgId: schoolId,
        schoolId,
        fullName: `${firstName || ""} ${lastName || ""}`.trim(),
        ownerUid: uid,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  /* ---- Ensure classroom doc exists (non-fatal) ---- */
  async function ensureClassroomDoc(className) {
    if (!className) return;
    try {
      await setDoc(
        doc(db, "schools", schoolId, "classrooms", className),
        { name: className, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.warn("ensureClassroomDoc failed (non-critical):", e?.message || e);
    }
  }

  /* ------------------------------- Add single ------------------------------- */
  const handleAddStudent = async () => {
    const { firstName, lastName, className } = newStudent;
    if (!firstName || !lastName || !className) {
      toast.error("All fields are required.");
      return;
    }
    if (!schoolId) {
      toast.error("No school is selected. Please return to the School Dashboard.");
      return;
    }
    if (isFull) {
      toast.error("You have no seats remaining on your plan.");
      return;
    }
    if (adding) return;

    const email = toEmailFromNames(firstName, lastName);
const username = uiUsernameFromEmail(email);  // UI-only (local part)

setAdding(true);
try {
  const { data } = await createStudentCallable({
    orgId: schoolId,
    schoolId,
    className,
    firstName,
    lastName,
    username: email,             // server expects a valid email here
    idempotencyKey: `${schoolId}:${email}`, // avoid double-create on quick repeats
  });

  const uid = data?.uid;
  const password = data?.password || "";

  if (!uid) throw new Error("UID not returned from server");

  try { await ensureClassroomDoc(className); } catch {}
  try { await stampStudentUserDoc(uid, { firstName, lastName, email }); } catch {}

  setStudents((prev) => [
    ...prev.filter((s) =>
      (s.username?.includes("@") ? s.username : `${s.username}@${DOMAIN}`) !== email
    ),
    { uid, firstName, lastName, className, username, password },
  ]);
      setNewStudent({ firstName: "", lastName: "", className: "" });
      setBulkText("");
      toast.success("✅ Student added.");
    } catch (err) {
      console.error("❌ Failed to add student:", err);
      toast.error(`❌ Failed to add student: ${msgFromError(err)}`);
    } finally {
      setAdding(false);
    }
  };

  /* ------------------------------- Delete one ------------------------------- */
  const handleDeleteStudent = (index) => {
    const student = students[index];
    if (!student || !student.uid) return;

    toast(
      (t) => (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffecb5",
            padding: "15px",
            borderRadius: "8px",
            color: "#664d03",
            fontFamily: "Montserrat, sans-serif",
          }}
        >
          Are you sure you want to delete <strong>{student.firstName}</strong>?
          <br />
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <button
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await deleteStudentCallable({
                    orgId: schoolId,
                    schoolId,
                    uid: student.uid,
                    className: student.className,
                  });
                  setStudents((prev) => prev.filter((s) => s.uid !== student.uid));
                  toast.success("✅ Student deleted.");
                } catch (err) {
                  console.warn("deleteStudentSeatCounted failed, falling back:", err?.message || err);
                  try {
                    await deleteDoc(doc(db, "students", student.uid));
                    if (student.className) {
                      await deleteDoc(
                        doc(db, "schools", schoolId, "classrooms", student.className, "students", student.uid)
                      );
                    }
                    setStudents((prev) => prev.filter((s) => s.uid !== student.uid));
                    toast.success("✅ Student deleted (fallback path).");
                  } catch (e2) {
                    console.error("❌ Deletion error:", e2);
                    toast.error("❌ Failed to delete student.");
                  }
                }
              }}
              style={{
                backgroundColor: "red",
                color: "white",
                padding: "5px 10px",
                borderRadius: "5px",
                border: "none",
              }}
            >
              Yes
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                backgroundColor: "#ccc",
                padding: "5px 10px",
                borderRadius: "5px",
                border: "none",
              }}
            >
              No
            </button>
          </div>
        </div>
      ),
      { duration: 10000 }
    );
  };


  /* ------------------------------- Reset password ------------------------------- */
  const handleResetPassword = async (student) => {
    if (!student || !student.uid) return;

    if (!schoolId) {
      toast.error("No school is selected. Please return to the School Dashboard.");
      return;
    }

    try {
      setResettingId(student.uid);

      const { data } = await resetPasswordCallable({
        orgId: schoolId,
        studentId: student.uid,
      });

      const newPassword = data?.newPassword;
      if (!newPassword) {
        throw new Error("No new password returned from server.");
      }

      // Update local table so the new password is visible + exportable
      setStudents((prev) =>
        prev.map((s) =>
          s.uid === student.uid ? { ...s, password: newPassword } : s
        )
      );

      // Best-effort copy to clipboard
      try {
        await navigator.clipboard.writeText(newPassword);
        toast.success(
          `New password for ${student.firstName} ${student.lastName}: ${newPassword} (copied to clipboard)`
        );
      } catch {
        toast.success(
          `New password for ${student.firstName} ${student.lastName}: ${newPassword}`
        );
      }
    } catch (err) {
      console.error("❌ Reset password failed:", err);
      toast.error(`❌ Failed to reset password: ${msgFromError(err)}`);
    } finally {
      setResettingId(null);
    }
  };



  /* ------------------------------- Bulk paste ------------------------------- */
const handleBulkTextUpload = async () => {
  try {
    if (!bulkText || !bulkText.trim()) {
      toast.error("Paste some students first.");
      return;
    }
    if (!schoolId) {
      toast.error("No school is selected. Please return to the School Dashboard.");
      return;
    }
    if (bulkAdding) return;

    let remaining = seatsRemaining;
    if (remaining <= 0) {
      toast.error("You have no seats remaining on your plan.");
      return;
    }

    setBulkAdding(true);

    const lines = bulkText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    let valid = 0;
    let ok = 0;
    let fail = 0;
    let firstError = null;

    // visible start feedback
    toast.loading(`Processing ${lines.length} lines…`, { id: "bulk" });

    for (const line of lines) {
      if (remaining <= 0) break;

     const parts = line
  .trim()
  .split(/[\t,]+| {2,}/)   // tabs, commas, or 2+ spaces
  .map((s) => s.trim())
  .filter(Boolean);

      const [firstName, lastName, className] = parts;

      if (!(firstName && lastName && className)) {
        // not a valid line
        continue;
      }
      valid++;

      const email = toEmailFromNames(firstName, lastName);
const username = uiUsernameFromEmail(email);

try {
  await ensureClassroomDoc(className);

  const { data } = await createStudentCallable({
    orgId: schoolId,
    schoolId,
    className,
    firstName,
    lastName,
    username: email,
    idempotencyKey: `${schoolId}:${email}`,
  });

  const uid = data?.uid;
  const password = data?.password || "";

  if (!uid) throw new Error("UID not returned from server");

  // best-effort
  try {
    await stampStudentUserDoc(uid, { firstName, lastName, email });
  } catch {}

  setStudents((prev) => [
    ...prev.filter(
      (s) =>
        (s.username?.includes("@") ? s.username : `${s.username}@${DOMAIN}`) !==
        email
    ),
    { uid, firstName, lastName, className, username, password },
  ]);

        ok++;
        remaining--;
      } catch (e) {
        fail++;
        const m = msgFromError(e);
        if (!firstError) firstError = m;
        console.error("Bulk create failed for:", line, e);
      }
    }

    // Finish + clear loader
    toast.dismiss("bulk");

    if (valid === 0) {
      toast.error(
        "No valid lines found. Use: FirstName, LastName, ClassName (tabs also supported)."
      );
      return;
    }

    if (ok > 0) {
      toast.success(`✅ Bulk complete: ${ok} added${fail ? `, ${fail} failed` : ""}.`);
      setBulkText("");
    } else {
      toast.error(
        `❌ Bulk failed: 0 added, ${fail} failed. First error: ${firstError || "Unknown"}`
      );
    }
  } finally {
    setBulkAdding(false);
  }
};


  /* -------------------------------- CSV flow ------------------------------- */
  const handleCSVUpload = async (e) => {
  const inputEl = e.target;               // ✅ capture immediately (don’t rely on pooled event)
  const file = inputEl.files?.[0];

  if (!file) return;

  if (!schoolId) {
    toast.error("No school selected.");
    inputEl.value = "";
    return;
  }

  if (seatsRemaining <= 0) {
    toast.error("You have no seats remaining on your plan.");
    inputEl.value = "";
    return;
  }

  if (csvAdding) return;

  setCsvAdding(true);
  const loadingId = toast.loading("Processing CSV…");

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    delimiter: "", // auto-detect
    delimitersToGuess: [",", ";", "\t", "|"], // ✅ handles UK Excel semicolon + tab
    complete: async (results) => {
      try {
        const rowsRaw = results.data || [];

        // ✅ Accept a few common header variations (so schools don’t break it)
        const rows = rowsRaw
          .map((row) => {
            const FirstName =
              row.FirstName || row.firstName || row["First Name"] || row["Firstname"] || "";
            const LastName =
              row.LastName || row.lastName || row["Last Name"] || row["Lastname"] || "";
            const ClassName =
              row.ClassName || row.className || row["Class Name"] || row["Classname"] || "";

            if (!FirstName || !LastName || !ClassName) return null;

            const email = toEmailFromNames(FirstName, LastName);
const username = uiUsernameFromEmail(email);

return {
  firstName: FirstName.trim(),
  lastName: LastName.trim(),
  className: ClassName.trim(),
  email,
  username,
};
          })
          .filter(Boolean);

        if (!rows.length) {
          toast.dismiss(loadingId);
          toast.error(
            "No valid rows found. Check headers: FirstName, LastName, ClassName."
          );
          return;
        }

        let remaining = seatsRemaining;
        let ok = 0;
        let fail = 0;

        for (const s of rows) {
          if (remaining <= 0) break;
          try {
            await ensureClassroomDoc(s.className);

            const { data } = await createStudentCallable({
  orgId: schoolId,
  schoolId,
  className: s.className,
  firstName: s.firstName,
  lastName: s.lastName,
  username: s.email,
  idempotencyKey: `${schoolId}:${s.email}`,
});

const uid = data?.uid;
const password = data?.password || "";

if (uid) {
  try {
    await stampStudentUserDoc(uid, {
      firstName: s.firstName,
      lastName: s.lastName,
      email: s.email,
    });
  } catch {}

  setStudents((prev) => [
    ...prev.filter(
      (p) =>
        (p.username?.includes("@")
          ? p.username
          : `${p.username}@${DOMAIN}`) !== s.email
    ),
    {
      uid,
      firstName: s.firstName,
      lastName: s.lastName,
      className: s.className,
      username: s.username,
      password,
    },
  ]);

              ok++;
              remaining -= 1;
            } else {
              fail++;
            }
          } catch (err) {
            console.error("CSV row failed:", s.email, err);
            fail++;
          }
        }

        toast.dismiss(loadingId);
        toast.success(`CSV complete: ${ok} added${fail ? `, ${fail} failed` : ""}.`);
      } finally {
        inputEl.value = "";       // ✅ lets you re-upload the same file again
        setCsvAdding(false);
      }
    },
    error: (err) => {
      console.error("Papa.parse error:", err);
      toast.dismiss(loadingId);
      toast.error(`CSV parse failed: ${err?.message || "Unknown error"}`);
      inputEl.value = "";
      setCsvAdding(false);
    },
  });
};


  /* ------------------------------ Edit modal ------------------------------- */
  const handleEditStudent = (student) => {
    setEditingStudent({ ...student });
    setShowEditModal(true);
  };

  const handleSaveStudentUpdate = async () => {
    const index = students.findIndex((s) => (s.uid || s.id) === editingStudent.uid);
    if (index === -1) return;

    const updated = [...students];
    updated[index] = editingStudent;
    setStudents(updated);

    try {
      await ensureClassroomDoc(editingStudent.className);

      await setDoc(doc(db, "students", editingStudent.uid), editingStudent, { merge: true });
      if (editingStudent.className) {
        await setDoc(
          doc(db, "schools", schoolId, "classrooms", editingStudent.className, "students", editingStudent.uid),
          editingStudent,
          { merge: true }
        );
      }
      toast.success("✅ Student updated successfully.");
    } catch (err) {
      console.error("❌ Failed to update student:", err);
      toast.error("❌ Failed to update student.");
    }
    setShowEditModal(false);
  };

  /* ---------------------------------- UI ----------------------------------- */
  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      {/* Header */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">Help</button>
          <button onClick={() => navigate("/")} className="header-button">Home</button>
          <button onClick={() => navigate("/school-dashboard")} className="header-button">Back to Dashboard</button>
        </nav>
      </header>

      <Toaster position="top-center" reverseOrder={false} />

      {/* Main */}
      <main className="page-content" style={{ textAlign: "center", padding: "20px", overflowX: "auto" }}>
        <div className="dashboard-card" style={{
          background: "rgba(255, 255, 255, 0.9)",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          maxWidth: "900px",
          margin: "0 auto",
          animation: "fadeIn 0.8s ease-in-out",
        }}>
          <h1 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", fontSize: "28px" }}>
            Manage Students
          </h1>

          {/* Seats meter */}
          <div style={{ marginBottom: 16 }}>
            <p>Maximum students allowed: <strong>{seatLimit}</strong></p>
            <p>Seats used: <strong>{students.length}</strong> / {seatLimit} &nbsp; (Remaining: {Math.max(seatLimit - students.length, 0)})</p>
            <div style={{ height: 10, background: "#eee", borderRadius: 6, overflow: "hidden", maxWidth: 480, margin: "0 auto" }}>
              <div
                style={{
                  width: `${Math.min(100, (students.length / Math.max(1, seatLimit)) * 100)}%`,
                  height: "100%",
                  background: students.length >= seatLimit ? "#ef4444" : "#3b82f6",
                }}
              />
            </div>
            {students.length >= seatLimit && (
              <div style={{ color: "#b45309", marginTop: 8 }}>You’ve reached your seat limit. Remove students or upgrade your plan.</div>
            )}
            {!schoolId && (
              <div style={{ color: "#b91c1c", marginTop: 8 }}>No school is selected. Go back to the School Dashboard first.</div>
            )}
          </div>

          {/* Filter Controls */}
          <div style={{ marginBottom: "20px", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px" }}>
            <label htmlFor="classFilter" style={{ fontWeight: "bold" }}>Select Class:</label>
            <select
              id="classFilter"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{ padding: "8px", borderRadius: "5px", border: "1px solid #ccc" }}
            >
              <option value="All">All</option>
              {Array.from(new Set(students.map((s) => s.className).filter(Boolean))).map((cls, idx) => (
                <option key={idx} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          {/* Add New Student */}
          <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", marginBottom: "20px" }}>
            Add a New Student
          </h2>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "20px", flexWrap: "wrap" }}>
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={newStudent.firstName}
              onChange={handleInputChange}
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif", width: isMobile ? "100%" : "auto" }}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={newStudent.lastName}
              onChange={handleInputChange}
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif", width: isMobile ? "100%" : "auto" }}
            />
            <input
              type="text"
              name="className"
              placeholder="Class Name (e.g., Class A)"
              value={newStudent.className}
              onChange={handleInputChange}
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif", width: isMobile ? "100%" : "auto" }}
            />
            <button
              onClick={handleAddStudent}
              className="student-button"
              disabled={isFull || !schoolId || adding}
              title={
                !schoolId ? "No school selected" :
                isFull ? "Seat limit reached" :
                adding ? "Adding…" : "Add student"
              }
              style={{ width: isMobile ? "100%" : "auto", opacity: adding ? 0.8 : 1 }}
            >
              {adding ? "Adding…" : "Add Student"}
            </button>
          </div>

          {/* Bulk Upload */}
          <div style={{ marginBottom: "20px" }}>
            <button
              onClick={() => setShowBulkUpload(!showBulkUpload)}
              className="bulk-button"
              title="Upload names in bulk via cut/paste or uploading CSV file"
              disabled={isFull || !schoolId}
            >
              {showBulkUpload ? "Hide Bulk Upload" : "Show Bulk Upload"}
            </button>

            {showBulkUpload && (
              <div style={{ marginTop: "10px" }}>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder="Enter student details as: FirstName, LastName, ClassName (one per line and ensure you add the commas)"
                  style={{ width: "100%", height: "100px", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif" }}
                />
                {bulkText.trim() !== "" && (
                  <div style={{ marginTop: "10px" }}>
                    <button onClick={handleBulkTextUpload} className="student-button" disabled={isFull || !schoolId || bulkAdding}>
                      {bulkAdding ? "Processing…" : "Proceed Upload"}
                    </button>
                  </div>
                )}
                <div style={{ marginTop: "10px" }}>
                  <label style={{ fontFamily: "Montserrat, sans-serif", fontSize: "14px" }}>
                    Or upload CSV:
                    <input type="file" accept=".csv" onChange={handleCSVUpload} style={{ marginLeft: "10px" }} disabled={isFull || !schoolId || csvAdding} />
                  </label>
                  {/* --- Bulk Upload Help (place this next to the CSV "Choose file" input) --- */}
<div style={{ marginTop: "12px", textAlign: "left" }}>
  <div
    style={{
      background: "rgba(255,255,255,0.95)",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: "12px 14px",
      fontFamily: "Montserrat, sans-serif",
      fontSize: 14,
      color: "#111827",
      lineHeight: 1.45,
      maxWidth: 760,
      margin: "0 auto",
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 6 }}>📌 Bulk Upload Guide</div>

    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>✍️ Option 1: Paste text (one student per line)</div>
      <div style={{ color: "#374151" }}>
        Use commas between fields like:
        <code style={{ marginLeft: 6, padding: "2px 6px", background: "#f3f4f6", borderRadius: 6 }}>
          FirstName, LastName, ClassName
        </code>
      </div>
      <pre
        style={{
          marginTop: 8,
          marginBottom: 0,
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          padding: "10px",
          borderRadius: 8,
          overflowX: "auto",
          fontSize: 13,
        }}
      >
{`John,Smith,Class A
Amy,Jones,Class B`}
      </pre>
    </div>

    <div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>📁 Option 2: Upload a CSV file (from Excel)</div>
      <div style={{ color: "#374151" }}>
        Your CSV must have these column headers:   FirstName LastName ClassName
        
      </div>
      <ol style={{ margin: "8px 0 0 18px", color: "#374151" }}>
        <li>In Excel, put First Name / Last Name / Class Name in separate columns</li>
        <li>File → Save As → <strong>CSV (Comma delimited) (.csv)</strong></li>
        <li>Upload the saved .csv file here</li>
      </ol>
      <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: 600 }}>
        ⚠️ .xlsx files are not supported (save as CSV first)
      </div>
    </div>
  </div>
</div>

                  {csvAdding && <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>Processing CSV…</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="dashboard-card" style={{
          background: "rgba(255, 255, 255, 0.9)",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          maxWidth: "1000px",
          margin: "30px auto",
        }}>
          <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif" }}
            >
              {["All", ...Array.from(new Set(students.map((s) => s.className).filter(Boolean)))].map((cls, index) => (
                <option key={index} value={cls}>{cls}</option>
              ))}
            </select>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="text"
                placeholder="Search student by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif", width: isMobile ? "100%" : "250px" }}
              />
              <button
                onClick={() => {
                  const list = selectedClass === "All" ? students : students.filter((s) => s.className === selectedClass);
                  if (!list.length) return;
                  const rows = list.map((s) => ({
                    FirstName: s.firstName,
                    LastName: s.lastName,
                    ClassName: s.className,
                    Username: s.username.includes("@") ? s.username : `${s.username}@${DOMAIN}`,
                    Password: s.password || "",
                    ...(includeStats
                      ? {
                          StoriesRead: s.storiesRead || 0,
                          TotalReadingTime: s.totalReadingTime || 0,
                          AverageReadingTime: s.averageReadingTime || 0,
                        }
                      : {}),
                  }));
                  const csv = Papa.unparse(rows);
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "students.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                disabled={(selectedClass === "All" ? students : students.filter((s) => s.className === selectedClass)).length === 0}
                className="export-button"
                title="Download the user name & passwords for the visible student list below as a CSV file"
              >
                Export to CSV File
              </button>
              <label style={{ fontFamily: "Montserrat, sans-serif", fontSize: "14px" }}>
                <input
                  type="checkbox"
                  checked={includeStats}
                  onChange={(e) => setIncludeStats(e.target.checked)}
                  style={{ marginRight: "6px" }}
                />
                Include extra stats
              </label>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Montserrat, sans-serif" }}>
              <thead>
                <tr style={{ backgroundColor: "#f4f4f4" }}>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>First Name</th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Last Name</th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Class Name</th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Username</th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Password</th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...new Map(
                  (selectedClass === "All" ? students : students.filter((s) => s.className === selectedClass))
                    .filter((s) => (`${s.firstName} ${s.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())))
                    .map((s) => [s.username, s])
                ).values()].map((student, index) => (
                  <tr
                    key={index}
                    style={{ transition: "background-color 0.3s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e8f0fe")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{student.firstName}</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{student.lastName}</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{student.className}</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                      {student.username.includes("@") ? student.username : `${student.username}@${DOMAIN}`}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{student.password}</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
  <div
    style={{
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      justifyContent: "center",
    }}
  >
    <button
      onClick={() => {
        const idx = students.findIndex((s) => s.uid === student.uid);
        if (idx >= 0) handleDeleteStudent(idx);
      }}
      className="delete-button"
      style={{ minWidth: 90 }}
    >
      Delete
    </button>
    <button
  onClick={() => handleEditStudent(student)}
  className="student-button"
  style={{ minWidth: 90 }}
  title="Edit this student"
>
  Edit
</button>

   <button
  onClick={() => handleResetPassword(student)}
  className="student-button"
  style={{ minWidth: 130, opacity: resettingId === student.uid ? 0.7 : 1 }}
  title="Generate a new password for this student"
  disabled={resettingId === student.uid}
>
  {resettingId === student.uid ? "Resetting…" : "Reset Password"}
</button>
  </div>
</td>


                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showEditModal && editingStudent && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999,
          }}>
            <div style={{ backgroundColor: "#fff", padding: "30px", borderRadius: "10px", maxWidth: "500px", width: "100%" }}>
              <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", marginBottom: "10px" }}>
                ✏️ Edit Student Details
              </h2>
              <p style={{ fontSize: "14px", marginBottom: "20px", color: "#333" }}>
                Update the student’s name or class and click <strong>Save</strong>.
              </p>

              <input
                value={editingStudent.firstName}
                onChange={(e) => setEditingStudent({ ...editingStudent, firstName: e.target.value })}
                placeholder="First Name"
                style={{ width: "100%", marginBottom: "10px", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif" }}
              />
              <input
                value={editingStudent.lastName}
                onChange={(e) => setEditingStudent({ ...editingStudent, lastName: e.target.value })}
                placeholder="Last Name"
                style={{ width: "100%", marginBottom: "10px", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif" }}
              />
              <input
                value={editingStudent.className}
                onChange={(e) => setEditingStudent({ ...editingStudent, className: e.target.value })}
                placeholder="Class Name"
                style={{ width: "100%", marginBottom: "10px", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontFamily: "Montserrat, sans-serif" }}
              />

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  onClick={() => setShowEditModal(false)}
                  style={{ backgroundColor: "#ccc", padding: "10px", borderRadius: "5px", border: "none", fontWeight: "bold", fontFamily: "Montserrat, sans-serif", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStudentUpdate}
                  style={{ backgroundColor: "#007BFF", color: "white", padding: "10px", borderRadius: "5px", border: "none", fontWeight: "bold", fontFamily: "Montserrat, sans-serif", cursor: "pointer" }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="page-footer" style={{ textAlign: "center", padding: "20px 0" }}>
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: "10px" }}>
          <a href="/about" className="footer-link">About Us</a>
          <a href="/contact" className="footer-link">Contact</a>
        </nav>
      </footer>
    </div>
  );
};

export default ManageStudents;
